import { nextHasUnsavedEdits } from '@src/services/creatorStudio/saveBeforeLeaving';
import { comparer, reaction } from 'mobx';
import { useCallback, useEffect, useRef } from 'react';

import type { EditorSaveState } from '@src/services/creatorStudio/saveBeforeLeaving';

/** The store state the tracker watches, and no more. */
export interface UnsavedEditsSource {
  readonly currentVideo?: { id?: string } | null;
  readonly isBackgroundSaving: boolean;
  readonly savingError?: unknown;
  readonly undoStack?: readonly unknown[];
  readonly redoStack?: readonly unknown[];
}

/**
 * PLAT-11929 — whether the loaded video has edits no save has taken yet.
 *
 * Creator Studio keeps no such flag, so this follows the store: every edit
 * moves the top of the undo or redo stack, and every save that starts takes
 * the edits made before it (see `nextHasUnsavedEdits`). Held in a ref, not
 * state: it is read at the moment someone leaves, and re-rendering the
 * toolbar on every edit would buy nothing.
 */
export function useCreatorStudioUnsavedEdits(
  store: UnsavedEditsSource,
): () => boolean {
  const unsavedRef = useRef(false);

  useEffect(() => {
    const read = (): EditorSaveState => ({
      videoId: store.currentVideo?.id ?? null,
      saving: store.isBackgroundSaving,
      failed: !!store.savingError,
      undoTop: store.undoStack?.at(-1),
      redoTop: store.redoStack?.at(-1),
    });
    let prev = read();
    unsavedRef.current = false;
    return reaction(
      read,
      next => {
        unsavedRef.current = nextHasUnsavedEdits(
          unsavedRef.current,
          prev,
          next,
        );
        prev = next;
      },
      { equals: comparer.shallow },
    );
  }, [store]);

  return useCallback(() => unsavedRef.current, []);
}
